import React from "react";
import { ShoppingCart, X } from "lucide-react";
import { Button } from "./ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";

export const Cart = ({ items, onRemoveItem, onCheckout }) => {
  const getTotalPrice = () => {
    return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const getTotalQuantity = () => {
    return items.reduce((sum, item) => sum + item.quantity, 0);
  };

  const getServiceFee = () => {
    return items.reduce((sum, item) => {
      if (item.price <= 0) return sum;
      const fee = item.event_service_fee !== undefined ? item.event_service_fee : 0.99;
      return sum + fee * item.quantity;
    }, 0);
  };

  const getGrandTotal = () => {
    return getTotalPrice() + getServiceFee();
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          data-testid="cart-btn"
          variant="outline"
          size="icon"
          className="relative"
        >
          <ShoppingCart className="w-5 h-5" />
          {getTotalQuantity() > 0 && (
            <span
              data-testid="cart-badge"
              className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center cart-badge"
            >
              {getTotalQuantity()}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Votre panier</SheetTitle>
          <SheetDescription>
            {getTotalQuantity() > 0
              ? `${getTotalQuantity()} article${getTotalQuantity() > 1 ? "s" : ""} dans votre panier`
              : "Votre panier est vide"}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-8 flex flex-col h-[calc(100vh-200px)]">
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <ShoppingCart className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <p>Votre panier est vide</p>
              </div>
            </div>
          ) : (
            <>
              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto space-y-4 mb-6">
                {items.map((item, index) => (
                  <div
                    key={index}
                    data-testid={`cart-item-${index}`}
                    className="glass-card rounded-lg p-4"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 pr-4">
                        <h4 className="font-semibold text-sm mb-1">
                          {item.event_title}
                        </h4>
                        <p className="text-xs text-muted-foreground mb-1">
                          {item.event_artist}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {item.event_date}
                        </p>
                      </div>
                      <Button
                        data-testid={`remove-item-${index}`}
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onRemoveItem(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-border">
                      <span className="text-xs text-muted-foreground">
                        {item.ticket_type} × {item.quantity}
                      </span>
                      <span className="font-bold text-primary">
                        {(item.price * item.quantity).toFixed(2)}€
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Cart Total & Checkout */}
              <div className="border-t border-border pt-4 space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Sous-total</span>
                    <span>{getTotalPrice().toFixed(2)}€</span>
                  </div>
                  {getServiceFee() > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Frais de service</span>
                      <span>{getServiceFee().toFixed(2)}€</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 border-t border-border">
                    <span className="text-lg font-semibold">Total</span>
                    <span className="text-2xl font-bold text-primary" data-testid="cart-total">
                      {getGrandTotal().toFixed(2)}€
                    </span>
                  </div>
                </div>
                <Button
                  data-testid="checkout-btn"
                  onClick={onCheckout}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-lg"
                >
                  Passer la commande
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default Cart;
